import { Children, isValidElement, ReactNode } from "react";

import {
  IOption,
  SelectDropdown,
} from "../../../components/SelectDropdown/SelectDropdown";
import { SelectFieldWrapper } from "./AdminPage.styles";

/** What a native `<select>` hands its onChange, and all this needs of it. */
export interface IAdminSelectChangeEvent {
  target: { value: string };
}

interface IAdminSelectFieldProps {
  /** Ties the field to its `FilterLabel`. */
  id?: string;
  /** A number where the caller holds a numeric id, as a native select allowed. */
  value: string | number;
  onChange: (event: IAdminSelectChangeEvent) => void;
  /** `<option>` elements, exactly as a native select takes them. */
  children: ReactNode;
  /** Marks the field touched, for the forms that validate on blur. */
  onBlur?: () => void;
  disabled?: boolean;
  /** px. 36 matches the admin form inputs; a page with larger fields passes its own. */
  height?: number;
}

/** Flattens an option's children — `{ward.id} - {ward.name}` and the like. */
function toText(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === "boolean") {
    return "";
  }
  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(toText).join("");
  }
  if (isValidElement(node)) {
    return toText((node.props as { children?: ReactNode }).children);
  }
  return "";
}

/** Reads `<option>` children into the shape the shared dropdown wants. */
function toOptions(children: ReactNode): IOption[] {
  return Children.toArray(children).flatMap((child, index) => {
    if (!isValidElement(child) || child.type !== "option") return [];

    const props = child.props as {
      value?: string | number;
      children?: ReactNode;
      disabled?: boolean;
    };
    const value = String(props.value ?? "");

    return [
      {
        value,
        label: toText(props.children),
        // An empty value is a legitimate option — the "Select a ward" row —
        // so the index keeps those keys unique.
        key: `${value}-${index}`,
        disabled: props.disabled,
      },
    ];
  });
}

/**
 * A searchable select for the /admin forms (EN-5146 UI feedback).
 *
 * These were native `<select>`s, which cannot be typed into: the ward picker
 * lists every ward in the region, so finding one meant scrolling a list of
 * twenty-odd in no useful order. The portal already had a searchable dropdown
 * — this is that component, wearing the admin form's dimensions.
 *
 * It deliberately keeps the native element's API: it takes the same `<option>`
 * children and hands back the same `{ target: { value } }`. That is what let
 * fifty-two call sites move over as a tag rename, with their option lists and
 * their handlers untouched — a conversion that can be read, rather than one
 * that rewrites every form in the section.
 */
export function AdminSelectField({
  id,
  value,
  onChange,
  children,
  onBlur,
  disabled = false,
  height = 36,
}: IAdminSelectFieldProps) {
  return (
    <SelectFieldWrapper onBlur={onBlur}>
      <SelectDropdown
        id={id}
        showSearch
        optionFilterProp="label"
        height={height}
        style={{ width: "100%" }}
        disabled={disabled}
        value={String(value ?? "")}
        options={toOptions(children)}
        // antd puts the label in `title` but leaves the value nowhere in the
        // DOM. A row is identified by what it MEANS — the value the form will
        // submit — not by the words it happens to display, so this puts it
        // where anything reading the page can find it.
        optionRender={(option) => (
          <span data-value={String(option.value ?? "")}>{option.label}</span>
        )}
        onValueChange={(selected) =>
          onChange({
            target: { value: Array.isArray(selected) ? selected[0] : selected },
          })
        }
      />
    </SelectFieldWrapper>
  );
}
